import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";

const CoursePage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="course-page">
      <SEOHead 
        title="The Authority Systems Course - OnPoint Authority Systems"
        description="The structured methodology behind the OnPoint Authority framework. This course prepares qualified operators and stakeholders for certification and licensed authority."
        url="https://onpointauthority.com/course"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-44 md:pb-24 px-6 md:px-12" data-testid="course-hero">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              The Authority<br />
              <span className="text-primary">Systems Course</span>
            </h1>
            <p className="text-xl md:text-2xl font-light leading-relaxed text-muted-foreground mb-6">
              The structured methodology behind the OnPoint Authority framework.
            </p>
            <p className="text-lg font-light text-foreground">
              This course prepares qualified operators and stakeholders for certification and licensed authority.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What This Course Is */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="what-course-is">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              What This Course Is
            </p>
            <div className="space-y-6 text-lg font-light leading-relaxed text-muted-foreground">
              <p>
                This course is not education in the traditional sense.<br />
                It is a structured walk-through of the authority architecture documented in The Authority Operating Manual.
              </p>
              <p className="text-foreground">
                The purpose of the course is to ensure conceptual alignment before certification is considered.
              </p>
              <div className="bg-card/5 border border-white/10 p-6 mt-8">
                <p className="text-sm text-muted-foreground mb-4 uppercase tracking-wider">Completion of this course:</p>
                <ul className="space-y-3">
                  {[
                    "Does not grant authority",
                    "Does not grant certification",
                    "Does not grant licensing",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-foreground">
                      <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2.5" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-primary mt-6 font-medium">
                  It establishes system comprehension only.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What This Course Covers */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="course-covers">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              What This Course Covers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                title: "Authority Design",
                desc: "How authority is defined, bounded, and governed.",
              },
              {
                title: "Certification Logic",
                desc: "Why authority must be proven before it is granted.",
              },
              {
                title: "Enforcement Mechanics",
                desc: "How systems maintain integrity without negotiation.",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <h3 className="font-serif text-2xl font-light text-foreground mb-4">
                  {item.title}
                </h3>
                <p className="text-muted-foreground">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Course Is For */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="course-audience">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              Who This Course Is For
            </p>
            <ul className="space-y-4 mb-8">
              {[
                "Operators preparing for certification",
                "Investors seeking system comprehension",
                "Board members evaluating governance integrity",
              ].map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start gap-3 text-lg text-foreground"
                >
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-3" />
                  {item}
                </motion.li>
              ))}
            </ul>
            <p className="text-muted-foreground italic">
              This course is not designed for casual learners.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Comes Next */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="course-next">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              What Comes Next
            </p>
            <p className="text-xl font-light text-foreground mb-8">
              Course completion is a prerequisite for certification consideration.
            </p>
            <Link 
              to="/certification" 
              className="inline-flex items-center text-sm text-primary hover:text-primary/80 transition-colors group"
              data-testid="view-certification-link"
            >
              View Certification Structure
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoursePage;
